import React, { useState } from "react";
import "../Dashboard/superManagerDashboard.css";
import { Layout, message, Spin } from "antd";
import { Accounts_Query, Role_Account_Query, GET_CLAN_CREW_DASHBOARD_VALUES } from "../../services/userAPI"; // Added new query
import { useLazyQuery, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import SearchableDropdown from "../../reusableField/searchableDropdownField";
import Editicon from "../../Images/Edit.png";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

const { Content } = Layout;

const ClanCrewDashboard = () => {
    const { loading: accountsLoading, error: accountsError, data: accountsData } = useQuery(Accounts_Query);
    const { loading: dashboardLoading, error: dashboardError, data: dashboardData } = useQuery(GET_CLAN_CREW_DASHBOARD_VALUES); // New query
    const navigate = useNavigate();
    const [selectedRoles, setSelectedRoles] = useState({});
    const [selectedValues, setSelectedValues] = useState({});
    const [loadingRoles, setLoadingRoles] = useState({});
    const [imageError, setImageError] = useState({});
    const [searchQuery, setSearchQuery] = useState("");

    const [fetchRoles] = useLazyQuery(Role_Account_Query, {
        fetchPolicy: 'network-only',
        onCompleted: (data, context) => {
            // Try to get accountId from multiple sources
            const roles = data?.getAllJobrole || [];
            let accountId = roles[0]?.account?.id;

            // If empty array, try to get from context
            if (!accountId && context?.variables) {
                accountId = context.variables.accountId;
            }

            console.log("Roles fetched:", { accountId, rolesCount: roles.length, data });

            if (accountId) {
                setSelectedRoles((prevRoles) => ({
                    ...prevRoles,
                    [accountId]: roles,
                }));
                setLoadingRoles((prev) => ({
                    ...prev,
                    [accountId]: false,
                }));
            }
        },
        onError: (error, context) => {
            console.error("Error fetching roles:", error);
            const accountId = context?.variables?.accountId || error.extraInfo?.variables?.accountId;
            if (accountId) {
                setSelectedRoles((prevRoles) => ({
                    ...prevRoles,
                    [accountId]: [],
                }));
                setLoadingRoles((prev) => ({
                    ...prev,
                    [accountId]: false,
                }));
            }
        },
    });

    const handleImageError = (userId) => {
        setImageError((prev) => ({
            ...prev,
            [userId]: true,
        }));
    };

    const handleAccountChange = (accountId) => {
        if (!selectedRoles.hasOwnProperty(accountId)) {
            console.log("Fetching roles for accountId:", accountId);
            setLoadingRoles((prev) => ({
                ...prev,
                [accountId]: true,
            }));

            fetchRoles({
                variables: { accountId: parseInt(accountId) },
            }).then((result) => {
                // Fallback: if onCompleted didn't handle it
                const roles = result?.data?.getAllJobrole || [];
                console.log("Fetch completed with roles:", roles.length);

                if (roles.length === 0 && !selectedRoles.hasOwnProperty(accountId)) {
                    setSelectedRoles((prevRoles) => ({
                        ...prevRoles,
                        [accountId]: [],
                    }));
                    setLoadingRoles((prev) => ({
                        ...prev,
                        [accountId]: false,
                    }));
                }
            }).catch((error) => {
                console.error("Fetch error:", error);
                setLoadingRoles((prev) => ({
                    ...prev,
                    [accountId]: false,
                }));
            });
        }
    };

    const handleRoleChange = (value, accountId) => {
        setSelectedValues((prev) => ({
            ...prev,
            [accountId]: value,
        }));
    };

    const handleViewRole = (accountId) => {
        const selectedRoleId = selectedValues[accountId];
        if (selectedRoleId) {
            const selectedRoleData = selectedRoles[accountId]?.find((role) => role.id === selectedRoleId);
            if (selectedRoleData) {
                navigate("/clan-crew-user-management", {
                    state: {
                        roleId: selectedRoleId,
                        dartsNeeded: selectedRoleData.dartsNeeded,
                        clanNeeded: selectedRoleData.clanNeeded,
                        syncNeeded: selectedRoleData.syncNeeded,
                        lenseNeeded: selectedRoleData.lenseNeeded || false,
                        emailLogin: selectedRoleData.emailLogin,
                        inputEditDays: selectedRoleData.inputEditDays,
                        mandatoryTimeDistribution: selectedRoleData.mandatoryTimeDistribution,
                        numberLogin: selectedRoleData.numberLogin,
                        name: selectedRoleData.name,
                        pocEmail: selectedRoleData.pocEmail || "",
                        pocName: selectedRoleData.pocName || "",
                        pocNumber: selectedRoleData.pocContactNumber || "",
                        targetEditDays: selectedRoleData.targetEditDays,
                        dartsFormNeeded: selectedRoleData.dartsFormNeeded,
                        accountId: selectedRoleData.account.id,
                        accountName: selectedRoleData.account?.name || "unknown",
                    },
                });
            }
        } else {
            message.warning("Please select a role first.");
        }
    };

    if (accountsLoading || dashboardLoading) return <div className="loading-container"><div className="spinner"></div></div>;
    if (accountsError || dashboardError) return <div>Error: {(accountsError || dashboardError).message}</div>;

    const filteredAccounts = accountsData?.allUserAccount
        ?.filter((user) => user.name.toLowerCase().includes(searchQuery.toLowerCase()))
        ?.reverse();

    const statsData = {
        clients: dashboardData?.getClanCrewDashboardValues?.totalClients || 0,
        roles: dashboardData?.getClanCrewDashboardValues?.totalRoles || 0,
        users: dashboardData?.getClanCrewDashboardValues?.totalUsers || 0,
        notifications: dashboardData?.getClanCrewDashboardValues?.totalNotifications || 0,
    };

    return (
        <Content className="bg-gray-100 min-h-screen">
            <div className="dashboard-container">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Dashboard</h2>
                <div className="dashboard-content">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Assigned Clients Card */}
                        <div className="bg-white p-3 rounded-lg border border-[#CECECE] h-20 relative">
                            <span className="flex items-center justify-end h-10">
                                <div
                                    style={{
                                        backgroundColor: '#5975FF0D', // Light beige background
                                        borderRadius: '50%', // Circular shape
                                        padding: '8px', // Space around the icon
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '50px', // Fixed width for circular shape
                                        height: '50px', // Fixed height for circular shape
                                    }}>
                                    <PeopleAltOutlinedIcon style={{
                                        fontSize: 30,
                                        color: '#3354FA', // Orange color for the bell icon
                                        backgroundColor: 'transparent', // Remove icon background
                                    }} />
                                </div>
                            </span>
                            <div className="absolute top-2 left-3">
                                <span style={{ fontFamily: 'Inter', color: '#000933BF' }}>Assigned Clients</span>
                            </div>
                            <div className="absolute bottom-0 top-8 left-3">
                                <p className="text-2xl font-bold text-gray-900">{statsData.clients}</p>
                            </div>
                        </div>

                        {/* Roles Card */}
                        <div className="bg-white p-3 rounded-lg border border-[#CECECE]  h-20 relative">
                            <span className="flex items-center justify-end h-10">
                                <div
                                    style={{
                                        backgroundColor: '#FDEDED96', // Light beige background
                                        borderRadius: '50%', // Circular shape
                                        padding: '8px', // Space around the icon
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '50px', // Fixed width for circular shape
                                        height: '50px', // Fixed height for circular shape
                                    }}
                                >
                                    <GroupAddOutlinedIcon style={{
                                        fontSize: 30,
                                        color: '#FF5255', // Orange color for the bell icon
                                        backgroundColor: 'transparent', // Remove icon background
                                    }} />
                                </div>
                            </span>
                            <div className="absolute top-2 left-3">
                                <span style={{ fontFamily: 'Inter', color: '#000933BF' }}>Roles</span>
                            </div>
                            <div className="absolute bottom-0 top-8 left-3">
                                <p className="text-2xl font-bold text-gray-900">{statsData.roles}</p>
                            </div>
                        </div>

                        {/* Users Card */}
                        <div className="bg-white p-3 rounded-lg border border-[#CECECE] h-20 relative">
                            <span className="flex items-center justify-end h-10">
                                <div
                                    style={{
                                        backgroundColor: '#b4bcb50d', // Light beige background
                                        borderRadius: '50%', // Circular shape
                                        padding: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '50px',
                                        height: '50px',
                                    }}>
                                    <PersonOutlinedIcon style={{
                                        fontSize: 30,
                                        color: '#21A600', // Orange color for the bell icon
                                        backgroundColor: 'transparent', // Remove icon background
                                    }} />
                                </div>
                            </span>
                            <div className="absolute top-2 left-3">
                                <span style={{ fontFamily: 'Inter', color: '#000933BF' }}>Users</span>
                            </div>
                            <div className="absolute bottom-0 top-8 left-3">
                                <p className="text-2xl font-bold text-gray-900">{statsData.users}</p>
                            </div>
                        </div>

                        {/* Notification Sent Card */}
                        <div className="bg-white p-3 rounded-lg border border-[#CECECE] h-20 relative">
                            <span className="flex items-center justify-end h-10">
                                <div
                                    style={{
                                        backgroundColor: '#FFF3E0', // Light beige background
                                        borderRadius: '50%', // Circular shape
                                        padding: '8px', // Space around the icon
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '50px', // Fixed width for circular shape
                                        height: '50px', // Fixed height for circular shape
                                    }}
                                >
                                    <NotificationsNoneOutlinedIcon
                                        style={{
                                            fontSize: 30,
                                            color: '#E29200', // Orange color for the bell icon
                                            backgroundColor: 'transparent', // Remove icon background
                                        }}
                                    />
                                </div>
                            </span>
                            <div className="absolute top-2 left-3">
                                <span style={{ fontFamily: 'Inter', color: '#000933BF' }}>Notification Sent</span>
                            </div>
                            <div className="absolute bottom-0 top-8 left-3">
                                <p className="text-2xl font-bold text-gray-900">{statsData.notifications}</p>
                            </div>
                        </div>
                    </div>
                    <br />
                    <div>
                        <h2>Clients</h2>
                    </div>
                    <Content>
                        <div className="cards-container-dashboard">
                            {filteredAccounts?.slice(0, 4).map((user) => {
                                const isRoleSelected = Boolean(selectedValues[user.id]);
                                const hasNoRoles = user.numberOfRoles === 0;
                                return (
                                    <div key={user.id} className="card">
                                        <div className="card-header">
                                            {imageError[user.id] || !user.mainLogoUrl ? (
                                                <div className="placeholderImage">
                                                    <span className="placeholder-text">No Image</span>
                                                </div>
                                            ) : (
                                                <img
                                                    src={user.mainLogoUrl}
                                                    alt={user.name}
                                                    className="card-image"
                                                    onError={() => handleImageError(user.id)}
                                                />
                                            )}
                                        </div>
                                        <h3 className="card-title">{user.name}</h3>
                                        <div className="card-actions">
                                            <SearchableDropdown
                                                options={selectedRoles[user.id] || []}
                                                label="name"
                                                id="id"
                                                selectedVal={selectedValues[user.id]}
                                                handleChange={(value) => handleRoleChange(value, user.id)}
                                                placeholder={
                                                    hasNoRoles && !loadingRoles[user.id]
                                                        ? "No roles found"
                                                        : loadingRoles[user.id]
                                                            ? "Loading roles..."
                                                            : "Select Role"
                                                }
                                                onFocus={() => handleAccountChange(user.id)}
                                                disabled={false}
                                                loading={loadingRoles[user.id]}
                                                notFoundContent={
                                                    hasNoRoles && !loadingRoles[user.id] ? "No roles found" : loadingRoles[user.id] ? <Spin size="small" /> : null
                                                }
                                            />
                                            <button
                                                className="view-role-button"
                                                onClick={() => handleViewRole(user.id)}
                                                disabled={!selectedValues[user.id] || hasNoRoles}
                                                style={{
                                                    backgroundColor: selectedValues[user.id]
                                                        ? "#AD0101"
                                                        : hasNoRoles
                                                            ? "#dcdcdc"
                                                            : "#FAC9CA",
                                                    cursor: selectedValues[user.id] ? "pointer" : "not-allowed",
                                                }}
                                            >
                                                View Role
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {filteredAccounts?.length > 3 && (
                            <div className="more-clients-container">
                                <button className="more-clients-btn" onClick={() => navigate("/clan-crew-home")}>
                                    More Clients
                                </button>
                            </div>
                        )}
                    </Content>
                </div>
            </div>
        </Content>
    );
};

export default ClanCrewDashboard;